import { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@components/shared/Button'
import { CloseIcon } from '@assets/svgs/CloseIcon'
import { FilterIcon } from '@assets/svgs/FilterIcon'
import { getInitialFilterValue } from '@pages/Preview/components/BigNumbersFilters/helpers/getInitialFilterValue'
import { MultiSelect } from '@components/shared/MultiSelect'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { useUser } from '@hooks/useUser'

import { getInitialMultiFilterValue } from './helpers/getInitialMultiFilterValue'
import { getSingleFilterOption, getTodosFilterValue } from './helpers/getSingleFilterOption'
import { initialFormValues } from './form/initialFormValues'

import type { AvailableFiltersItemProps } from '@services/api/preview/types'
import type { PreviewManagementFiltersFormProps } from './form/types'
import type { PreviewManagementFiltersProps } from './types'

import * as apiPreviewManagement from '@services/api/previewManagement'
import * as S from './styles'

const toSortedKey = (values?: string[]) => [...(values ?? [])].sort().join('|')

export const PreviewManagementFilters = ({ onFilter }: PreviewManagementFiltersProps) => {
  const { filiData, setFiliData } = useUser()

  const methods = useForm<PreviewManagementFiltersFormProps>({
    defaultValues: initialFormValues
  })

  const businessUnit = methods.watch('businessUnit')
  const selectedBusinessUnit = businessUnit && businessUnit !== 'all' ? businessUnit : null

  const directorate = methods.watch('directorate') ?? []
  const portfolio = methods.watch('portfolio') ?? []
  const economicGroup = methods.watch('economicGroup') ?? []
  const segment = methods.watch('segment') ?? []
  const serviceType = methods.watch('serviceType') ?? []
  const goName = methods.watch('goName') ?? []

  // "Todos" não restringe o nível abaixo; só conta como seleção quem tem opção real marcada
  const hasRealSelection = (values: string[]) => values.some((value) => value !== 'all')

  const hasDirectorateSelection = hasRealSelection(directorate)
  const hasPortfolioSelection = hasRealSelection(portfolio)
  const hasEconomicGroupSelection = hasRealSelection(economicGroup)
  const hasSegmentSelection = hasRealSelection(segment)
  const hasServiceTypeSelection = hasRealSelection(serviceType)
  const hasGoNameSelection = hasRealSelection(goName)

  // Depois do "Limpar", o Tipo de Serviço fica no estado neutro ("Todos") enquanto o Centro de
  // Lucro continuar em "Todos"; ao escolher um CL específico a opção única volta a ser forçada
  const clearedFiliRef = useRef<string | null>(null)
  const isClearedState = () => !!clearedFiliRef.current && clearedFiliRef.current === filiData?.filiCode

  const getServiceTypeInitialValue = (options?: AvailableFiltersItemProps[]) =>
    isClearedState() ? getTodosFilterValue(options) : getInitialMultiFilterValue(options ?? [])

  const directorateKey = toSortedKey(directorate)
  const portfolioKey = toSortedKey(portfolio)
  const economicGroupKey = toSortedKey(economicGroup)
  const segmentKey = toSortedKey(segment)
  const serviceTypeKey = toSortedKey(serviceType)
  const goNameKey = toSortedKey(goName)

  // Business Unit é opcional em toda a cascata: entra no payload só quando há um selecionado
  const businessUnitParam = selectedBusinessUnit ? { businessUnit: [selectedBusinessUnit] } : {}
  const realValues = (values: string[]) => values.filter((value) => value !== 'all')

  // Fonte fixa das opções de Business Unit e dos valores iniciais do form, escopada pelo CL do header
  const { data: baseFiltersData, isLoading: baseFiltersLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode],
    queryFn: () => apiPreviewManagement.getPreviewManagementFilters({ branchCodes: [filiData!.filiCode] }),
    enabled: !!filiData?.filiCode
  })

  // Diretoria (e Nome GO, que não depende de mais nada abaixo) dependem só do Business Unit
  const { data: afterBusinessUnitData, isLoading: afterBusinessUnitLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode, 'bu', selectedBusinessUnit],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!]
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit
  })

  const directorateSourceData = selectedBusinessUnit ? afterBusinessUnitData : baseFiltersData
  const directorateSourceLoading = selectedBusinessUnit ? afterBusinessUnitLoading : baseFiltersLoading

  // Grupo Econômico depende do Business Unit + Diretoria selecionada
  const { data: afterDirectorateData, isLoading: afterDirectorateLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode, 'bu', selectedBusinessUnit, 'directorate', directorateKey],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate)
      }),
    enabled: !!filiData?.filiCode && hasDirectorateSelection
  })

  const portfolioSourceData =
    hasDirectorateSelection ? afterDirectorateData : directorateSourceData
  const portfolioSourceLoading =
    hasDirectorateSelection ? afterDirectorateLoading : directorateSourceLoading

  // Grupo Econômico depende do Business Unit + Diretoria + Portfólio selecionados
  const { data: afterPortfolioData, isLoading: afterPortfolioLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate),
        portfolio: realValues(portfolio)
      }),
    enabled: !!filiData?.filiCode && hasPortfolioSelection
  })

  const economicGroupSourceData =
    hasPortfolioSelection ? afterPortfolioData : portfolioSourceData
  const economicGroupSourceLoading =
    hasPortfolioSelection ? afterPortfolioLoading : portfolioSourceLoading

  // Segmento depende do Business Unit + Diretoria + Grupo Econômico selecionados
  const { data: afterEconomicGroupData, isLoading: afterEconomicGroupLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate),
        portfolio: realValues(portfolio),
        economicGroup: realValues(economicGroup)
      }),
    enabled: !!filiData?.filiCode && hasEconomicGroupSelection
  })

  const segmentSourceData =
    hasEconomicGroupSelection ? afterEconomicGroupData : economicGroupSourceData
  const segmentSourceLoading =
    hasEconomicGroupSelection ? afterEconomicGroupLoading : economicGroupSourceLoading

  // Tipo de Serviço depende do Business Unit + Diretoria + Grupo Econômico + Segmento selecionados
  const { data: afterSegmentData, isLoading: afterSegmentLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey,
      'segment',
      segmentKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate),
        portfolio: realValues(portfolio),
        economicGroup: realValues(economicGroup),
        product: realValues(segment)
      }),
    enabled: !!filiData?.filiCode && hasSegmentSelection
  })

  const serviceTypeSourceData = hasSegmentSelection ? afterSegmentData : segmentSourceData
  const serviceTypeSourceLoading =
    hasSegmentSelection ? afterSegmentLoading : segmentSourceLoading

  // Nome GO depende de todos os níveis acima, incluindo o Tipo de Serviço
  const { data: afterServiceTypeData, isLoading: afterServiceTypeLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey,
      'segment',
      segmentKey,
      'serviceType',
      serviceTypeKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate),
        portfolio: realValues(portfolio),
        economicGroup: realValues(economicGroup),
        product: realValues(segment),
        serviceType: realValues(serviceType)
      }),
    enabled: !!filiData?.filiCode && hasServiceTypeSelection
  })

  const goNameSourceData = hasServiceTypeSelection ? afterServiceTypeData : serviceTypeSourceData
  const goNameSourceLoading = hasServiceTypeSelection ? afterServiceTypeLoading : serviceTypeSourceLoading

  // Nome GCE depende do Nome GO selecionado (Business Unit é opcional, entra no filtro só se selecionado)
  const { data: afterGoNameData, isLoading: afterGoNameLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey,
      'segment',
      segmentKey,
      'serviceType',
      serviceTypeKey,
      'goName',
      goNameKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...businessUnitParam,
        boardOfDirectors: realValues(directorate),
        portfolio: realValues(portfolio),
        economicGroup: realValues(economicGroup),
        product: realValues(segment),
        serviceType: realValues(serviceType),
        regionalManager: realValues(goName)
      }),
    enabled: !!filiData?.filiCode && hasGoNameSelection
  })

  const gceNameSourceData = hasGoNameSelection ? afterGoNameData : goNameSourceData
  const gceNameSourceLoading = hasGoNameSelection ? afterGoNameLoading : goNameSourceLoading

  // Quando uma categoria só tem 1 opção real, ela já vem pré-selecionada e aplicada
  useEffect(() => {
    if (!baseFiltersData) return

    const initialValues: PreviewManagementFiltersFormProps = {
      businessUnit: getInitialFilterValue(baseFiltersData.businessUnit),
      directorate: getInitialMultiFilterValue(baseFiltersData.boardOfDirectors),
      portfolio: getInitialMultiFilterValue(baseFiltersData.portfolio),
      economicGroup: getInitialMultiFilterValue(baseFiltersData.economicGroup),
      segment: getInitialMultiFilterValue(baseFiltersData.product),
      serviceType: getServiceTypeInitialValue(baseFiltersData.serviceType),
      goName: getInitialMultiFilterValue(baseFiltersData.regionalManager),
      gceName: getInitialMultiFilterValue(baseFiltersData.areaManager)
    }

    methods.reset(initialValues)
    onFilter(initialValues)
  }, [baseFiltersData])

  // Ao trocar o Business Unit, reseta Diretoria, Portfólio, Grupo Econômico, Segmento, Tipo de Serviço, Nome GO e Nome GCE
  const lastBusinessUnitRef = useRef<string | null>(null)
  useEffect(() => {
    if (!directorateSourceData || lastBusinessUnitRef.current === selectedBusinessUnit) return

    lastBusinessUnitRef.current = selectedBusinessUnit

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: getInitialMultiFilterValue(directorateSourceData.boardOfDirectors),
      portfolio: getInitialMultiFilterValue(directorateSourceData.portfolio),
      economicGroup: getInitialMultiFilterValue(directorateSourceData.economicGroup),
      segment: getInitialMultiFilterValue(directorateSourceData.product),
      serviceType: getServiceTypeInitialValue(directorateSourceData.serviceType),
      goName: getInitialMultiFilterValue(directorateSourceData.regionalManager),
      gceName: getInitialMultiFilterValue(directorateSourceData.areaManager)
    })
  }, [selectedBusinessUnit, directorateSourceData])

  // Ao trocar a Diretoria, reseta Portfólio, Grupo Econômico, Segmento e Tipo de Serviço
  const lastDirectorateRef = useRef<string>('')
  useEffect(() => {
    if (!portfolioSourceData || lastDirectorateRef.current === directorateKey) return

    lastDirectorateRef.current = directorateKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: getInitialMultiFilterValue(portfolioSourceData.portfolio),
      economicGroup: getInitialMultiFilterValue(portfolioSourceData.economicGroup),
      segment: getInitialMultiFilterValue(portfolioSourceData.product),
      serviceType: getServiceTypeInitialValue(portfolioSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [directorateKey, portfolioSourceData])

  // Ao trocar o Portfólio, reseta Grupo Econômico, Segmento e Tipo de Serviço
  const lastPortfolioRef = useRef<string>('')
  useEffect(() => {
    if (!economicGroupSourceData || lastPortfolioRef.current === portfolioKey) return

    lastPortfolioRef.current = portfolioKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: getInitialMultiFilterValue(economicGroupSourceData.economicGroup),
      segment: getInitialMultiFilterValue(economicGroupSourceData.product),
      serviceType: getServiceTypeInitialValue(economicGroupSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [portfolioKey, economicGroupSourceData])

  // Ao trocar o Grupo Econômico, reseta Segmento e Tipo de Serviço
  const lastEconomicGroupRef = useRef<string>('')
  useEffect(() => {
    if (!segmentSourceData || lastEconomicGroupRef.current === economicGroupKey) return

    lastEconomicGroupRef.current = economicGroupKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: getInitialMultiFilterValue(segmentSourceData.product),
      serviceType: getServiceTypeInitialValue(segmentSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [economicGroupKey, segmentSourceData])

  // Ao trocar o Segmento, reseta Tipo de Serviço
  const lastSegmentRef = useRef<string>('')
  useEffect(() => {
    if (!serviceTypeSourceData || lastSegmentRef.current === segmentKey) return

    lastSegmentRef.current = segmentKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: methods.getValues('segment'),
      serviceType: getServiceTypeInitialValue(serviceTypeSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [segmentKey, serviceTypeSourceData])

  // Tipo de Serviço com uma única opção real fica forçadamente selecionado, mesmo se a lista de
  // opções não mudar entre trocas de Centro de Lucro / Business Unit / Diretoria / GE / Segmento
  const singleServiceType = getSingleFilterOption(serviceTypeSourceData?.serviceType)
  useEffect(() => {
    if (!singleServiceType || isClearedState()) return
    if (serviceType.length === 1 && serviceType[0] === singleServiceType.code) return

    methods.setValue('serviceType', [singleServiceType.code])
  }, [singleServiceType?.code, serviceType])

  // Ao trocar o Nome GO, reseta Nome GCE
  const lastGoNameRef = useRef<string>('')
  useEffect(() => {
    if (!gceNameSourceData || lastGoNameRef.current === goNameKey) return

    lastGoNameRef.current = goNameKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: methods.getValues('segment'),
      serviceType: methods.getValues('serviceType'),
      goName: methods.getValues('goName'),
      gceName: getInitialMultiFilterValue(gceNameSourceData.areaManager)
    })
  }, [goNameKey, gceNameSourceData])

  const toOptions = (items?: { code: string; name: string }[]) =>
    items?.map((item) => ({ label: item.name, value: item.code })) || []

  const onSubmit = (formData: PreviewManagementFiltersFormProps) => onFilter(formData)

  return (
    <FormProvider {...methods}>
      <RowBox columns={4} marginTop={24} marginBottom={24}>
        <Select
          name="businessUnit"
          label="Business Unit"
          placeholder="Todos"
          options={toOptions(baseFiltersData?.businessUnit)}
          loading={baseFiltersLoading}
        />

        <MultiSelect
          name="directorate"
          label="Diretoria"
          placeholder="Selecione..."
          options={toOptions(directorateSourceData?.boardOfDirectors)}
          loading={directorateSourceLoading}
        />

        <MultiSelect
          name="portfolio"
          label="Portfólio"
          placeholder="Selecione..."
          options={toOptions(portfolioSourceData?.portfolio)}
          loading={portfolioSourceLoading}
        />

        <MultiSelect
          name="economicGroup"
          label="Grupo Econômico"
          placeholder="Selecione..."
          options={toOptions(economicGroupSourceData?.economicGroup)}
          loading={economicGroupSourceLoading}
        />

        <MultiSelect
          name="segment"
          label="Segmento"
          placeholder="Selecione..."
          options={toOptions(segmentSourceData?.product)}
          loading={segmentSourceLoading}
        />

        <MultiSelect
          name="serviceType"
          label="Tipo de Serviço"
          placeholder="Selecione..."
          options={toOptions(serviceTypeSourceData?.serviceType)}
          loading={serviceTypeSourceLoading}
        />

        <MultiSelect
          name="goName"
          label="Nome GO"
          placeholder="Selecione..."
          options={toOptions(goNameSourceData?.regionalManager)}
          loading={goNameSourceLoading}
        />

        <MultiSelect
          name="gceName"
          label="Nome GCE"
          placeholder="Selecione..."
          options={toOptions(gceNameSourceData?.areaManager)}
          loading={gceNameSourceLoading}
        />

        <S.FilterButtons>
          <Button
            width="100%"
            colorType="outlineBlue"
            rightIcon={<FilterIcon />}
            onClick={() => methods.handleSubmit(onSubmit)()}
          >
            Filtrar
          </Button>

          <Button
            width="100%"
            colorType="outlineBlue"
            rightIcon={<CloseIcon />}
            onClick={() => {
              clearedFiliRef.current = 'ALL'

              const clearedValues: PreviewManagementFiltersFormProps = {
                ...initialFormValues,
                serviceType: getTodosFilterValue(serviceTypeSourceData?.serviceType)
              }

              methods.reset(clearedValues)
              onFilter(clearedValues)

              if (filiData && filiData.filiCode !== 'ALL') {
                setFiliData({ ...filiData, filiCode: 'ALL', filiName: 'Todos' })
              }
            }}
          >
            Limpar
          </Button>
        </S.FilterButtons>
      </RowBox>
    </FormProvider>
  )
}
